/* Alternix journal search. Uses the same Shopify endpoint as the original working search. */
class BlogSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.input = this.querySelector('#blog-search-input');
    this.form = this.querySelector('form');
    this.resetButton = this.querySelector('#blog-search-reset');
    this.results = this.querySelector('#results');
    this.resultsTitle = this.results?.querySelector('.title');
    this.resultsGrid = this.results?.querySelector('.inner');
    this.page = this.closest('.main-blog');
    if (!this.input || !this.form || !this.resetButton || !this.results || !this.resultsTitle || !this.resultsGrid) return;

    this.form.addEventListener('submit', event => this.getSearch(event));
    this.resetButton.addEventListener('click', () => this.resetAll());
    this.input.addEventListener('input', () => {
      this.dataset.query = this.input.value;
      if (!this.input.value.trim()) this.resetAll();
    });
  }

  async getSearch(event) {
    event.preventDefault();
    const query = this.input.value.trim();
    if (!query) { this.resetAll(); return; }
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.dataset.query = query;
    this.showResults();
    this.results.setAttribute('aria-busy', 'true');
    this.resultsTitle.textContent = `Searching for “${query}”…`;
    this.resultsGrid.replaceChildren();
    this.removeNote();

    // Deliberately preserve the request format used by Alternix's previously working script.
    // Adding predictive-search options can produce failures on some Shopify configurations.
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=article`;
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      if (controller !== this.abortController) return;
      const articles = payload?.resources?.results?.articles;
      if (!Array.isArray(articles)) throw new Error('Unexpected Shopify search response');
      this.renderResults(articles, query);
    } catch (error) {
      if (error.name === 'AbortError' || controller !== this.abortController) return;
      console.error('[Alternix blog search]', error);
      this.resultsTitle.textContent = 'Search unavailable';
      this.setMessage('We couldn’t load the articles. Please try again.');
    } finally {
      if (controller === this.abortController) this.results.removeAttribute('aria-busy');
    }
  }

  showResults() {
    this.results.classList.add('active');
    this.resetButton.classList.remove('visually-hidden');
    this.page?.classList.add('blog-is-searching');
  }

  renderResults(articles, query) {
    this.results.dataset.count = String(articles.length);
    this.resultsTitle.textContent = articles.length ? `Results for “${query}”` : `No results for “${query}”`;
    this.resultsGrid.replaceChildren();
    if (!articles.length) { this.setMessage('Try another brand, product or topic.'); return; }
    const fragment = document.createDocumentFragment();
    articles.forEach(article => fragment.append(this.makeCard(article)));
    this.resultsGrid.append(fragment);
  }

  makeCard(article) {
    const outer = document.createElement('div');
    outer.className = 'blog-articles__article article';
    const card = document.createElement('article');
    card.className = 'blog-card';
    const inner = document.createElement('div');
    inner.className = 'blog-card__inner';
    const href = this.safeUrl(article.url);
    const img = article.featured_image?.url || article.image?.url || '';
    const imageUrl = this.safeUrl(img);
    if (imageUrl !== '#' && imageUrl) {
      const imageLink = document.createElement('a');
      imageLink.href = href;
      imageLink.className = 'blog-card__image-wrapper';
      imageLink.setAttribute('aria-hidden', 'true');
      imageLink.tabIndex = -1;
      const image = document.createElement('img');
      image.className = 'blog-card__image';
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      imageLink.append(image);
      inner.append(imageLink);
    }
    const content = document.createElement('div');
    content.className = 'blog-card__content';
    const tags = Array.isArray(article.tags) ? article.tags : [];
    const category = ['Guide', 'Explainer', 'Reviews', 'News', 'Best Picks'].find(tag => tags.includes(tag));
    if (category) {
      const info = document.createElement('div');
      info.className = 'blog-card__info';
      const badge = document.createElement('span');
      badge.className = 'blog-card__category';
      badge.textContent = category;
      info.append(badge);
      content.append(info);
    }
    const heading = document.createElement('h2');
    heading.className = 'blog-card__heading';
    const headingLink = document.createElement('a');
    headingLink.href = href;
    headingLink.textContent = article.title || 'Read article';
    heading.append(headingLink);
    content.append(heading);
    const description = this.stripHtml(article.summary_html || article.body || '');
    if (description) {
      const excerpt = document.createElement('p');
      excerpt.className = 'blog-card__excerpt';
      excerpt.textContent = description.length > 165 ? description.slice(0, 162).trimEnd() + '…' : description;
      content.append(excerpt);
    }
    const read = document.createElement('a');
    read.className = 'blog-card__read-more';
    read.href = href;
    read.textContent = 'Read article →';
    content.append(read);
    inner.append(content);
    card.append(inner);
    outer.append(card);
    return outer;
  }

  stripHtml(input) {
    const element = document.createElement('div');
    element.innerHTML = input;
    return (element.textContent || '').replace(/\s+/g, ' ').trim();
  }

  safeUrl(raw) {
    if (!raw) return '#';
    try {
      const url = new URL(raw, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch { return '#'; }
  }

  setMessage(message) {
    this.resultsGrid.replaceChildren();
    const paragraph = document.createElement('p');
    paragraph.className = 'blog-search-message';
    paragraph.textContent = message;
    this.resultsGrid.append(paragraph);
  }

  removeNote() {
    this.querySelectorAll('.blog-search-limitation').forEach(note => note.remove());
  }

  resetAll() {
    this.abortController?.abort();
    this.abortController = null;
    this.input.value = '';
    this.dataset.query = '';
    this.results.classList.remove('active');
    this.results.removeAttribute('aria-busy');
    this.results.dataset.count = '';
    this.resultsTitle.textContent = '';
    this.resultsGrid.replaceChildren();
    this.resetButton.classList.add('visually-hidden');
    this.page?.classList.remove('blog-is-searching');
    this.removeNote();
  }
}

if (!customElements.get('blog-search')) customElements.define('blog-search', BlogSearch);
